import type { FC } from "react";

import { Column, Row } from "@jti/core-components/components/Row";
import type { SecuredFieldsPayment } from "@jti/store/slices/checkoutV2Slice";
import { useTranslations } from "@jti/ui";

import { formatCardSummary } from "../../utils/formatCardSummary";
import { LoyaltyPreviewDescription } from "../LoyaltyPreviewDescription";
import type { PaymentOption } from "../PaymentMethodsSection";

import type { LoyaltyPreviewInfo } from "./PaymentMethodsSectionPreview.types";

import styles from "./PaymentMethodsSectionPreview.module.scss";

const getPaymentMethodLabel = (
  securedFieldsPayment: SecuredFieldsPayment | undefined,
  selectedMethod: PaymentOption | undefined,
  selectedPaymentMethodCode: string | undefined
): string => {
  if (
    securedFieldsPayment &&
    securedFieldsPayment.paymentMethodCode === selectedPaymentMethodCode &&
    securedFieldsPayment.cardDetails
  ) {
    return formatCardSummary(securedFieldsPayment.cardDetails);
  }

  return selectedMethod?.title ?? selectedPaymentMethodCode ?? "";
};

type PaymentMethodsSectionPreviewProps = {
  paymentOptions: PaymentOption[];
  selectedPaymentMethodCode?: string;
  isLoyaltyInfoVisible: boolean;
  loyaltyInfo?: LoyaltyPreviewInfo;
  isRedeemVisible?: boolean;
  securedFieldsPayment?: SecuredFieldsPayment;
};

export const PaymentMethodsSectionPreview: FC<PaymentMethodsSectionPreviewProps> = ({
  paymentOptions,
  selectedPaymentMethodCode,
  isLoyaltyInfoVisible = false,
  loyaltyInfo,
  isRedeemVisible = true,
  securedFieldsPayment,
}) => {
  const { __ } = useTranslations();

  const selectedMethod = paymentOptions.find((option) => option.code === selectedPaymentMethodCode);

  const paymentMethodLabel = getPaymentMethodLabel(securedFieldsPayment, selectedMethod, selectedPaymentMethodCode);

  return (
    <Row className={styles.wrapper}>
      {isLoyaltyInfoVisible && (
        <Column xs={12} md={6}>
          <p className={styles.title}>{__("cart.checkout.loyalty.preview.title")}</p>
          <p className={styles.subtitle}>
            <LoyaltyPreviewDescription loyaltyInfo={loyaltyInfo} isRedeemVisible={isRedeemVisible} />
          </p>
        </Column>
      )}
      <Column xs={12} md={6}>
        <p className={styles.title}>{__("cart.order.payment_method.title")}</p>
        <p className={styles.subtitle}>{paymentMethodLabel}</p>
      </Column>
    </Row>
  );
};
